import * as go from 'gojs';
import { noop } from 'vet-bones/utils';

import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  DELETED_OUTLINE_COLOR,
  DRAFT_OUTLINE_COLOR,
  LINK_ARROW_SCALE,
  LINK_COLOR,
  MAPPING_DASH_SIZE,
  MAPPING_DASH_SPACING,
  MAPPING_LINK_SELECTED_WIDTH,
  MAPPING_LINK_WIDTH,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import { ExtendedLink } from 'src/containers/ProjectDiagram/templates/extensions/link';
import {
  dataBinding,
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isMappingModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  isMappingVisible,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import {
  DeletedMapping,
  Mapping,
} from 'src/containers/ProjectDiagram/types/data';
import { MappingLink } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';

const determineMappingColor = (deleted: boolean) =>
  determineColorFromSelection(
    isMappingModified,
    deleted ? DELETED_OUTLINE_COLOR : LINK_COLOR,
    deleted ? DELETED_OUTLINE_COLOR : DRAFT_OUTLINE_COLOR
  );

const $ = go.GraphObject.make;

export const createMappingTemplate = ({
  deleted = false,

  contextMenu,
  handleClick = noop,
  handleDoubleClick = noop,
}: {
  deleted?: boolean;

  contextMenu: go.HTMLInfo;
  handleClick?: NonNullable<MappingLink['doubleClick']>;
  handleDoubleClick?: NonNullable<MappingLink['doubleClick']>;
}) =>
  $(
    ExtendedLink,
    {
      click: handleClick,
      contextMenu,
      cursor: 'pointer',
      curve: go.Curve.Bezier,
      doubleClick: handleDoubleClick,
      layerName: Layer.MAPPING_BACKGROUND_LAYER,
      mouseEnter: graphObjectMouseEnter,
      mouseLeave: graphObjectMouseLeave,
      movable: false,
      selectionAdorned: false,
      toShortLength: LINK_ARROW_SCALE * MAPPING_LINK_WIDTH,
    },

    dataBinding<go.Link, Mapping | DeletedMapping>()('points').makeTwoWay(),
    dataBinding<go.Link, Mapping | DeletedMapping>()(
      'relinkableTo',
      '',
      (data) => !data.to
    ),
    objectBinding<go.Link, go.Link>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.MAPPING_FOREGROUND_LAYER
        : Layer.MAPPING_BACKGROUND_LAYER
    ),
    modelBinding<go.Shape, ModelData>()('visible', '', isMappingVisible),

    $(
      go.Shape, // the line
      {
        name: 'MAIN',
        isPanelMain: true,
        stroke: LINK_COLOR,
        strokeDashArray: [MAPPING_DASH_SIZE, MAPPING_DASH_SPACING],
        strokeWidth: MAPPING_LINK_WIDTH,
      },

      modelBinding<go.Shape, ModelData>()(
        'stroke',
        '',
        determineMappingColor(deleted)
      ),
      objectBinding<go.Shape, go.Link>()('strokeWidth', '', (link) =>
        link.isHighlighted || link.isSelected
          ? MAPPING_LINK_SELECTED_WIDTH
          : MAPPING_LINK_WIDTH
      )
    ),

    $(
      go.Shape, // the arrow head
      {
        name: 'ARROW',
        toArrow: 'Triangle',
        fill: LINK_COLOR,
        stroke: LINK_COLOR,
        scale: LINK_ARROW_SCALE,
      },
      modelBinding<go.Shape, ModelData>()(
        'fill',
        '',
        determineMappingColor(deleted)
      ),
      modelBinding<go.Shape, ModelData>()(
        'stroke',
        '',
        determineMappingColor(deleted)
      ),
      objectBinding<go.Shape, go.Link>()('scale', '', (link) =>
        link.isHighlighted || link.isSelected
          ? LINK_ARROW_SCALE * 1.5
          : LINK_ARROW_SCALE
      )
    )
  );
